/**
 * @file CustomClinicRows.tsx
 * @copyright 2023 Boston Scientific Corporation
 * ********************************************************************************************************************
 * Copyright © 2023 Boston Scientific Corporation All rights reserved.
 *
 * This file and its contents are a product of Boston Scientific Corporation.  All software distributed by Boston
 * Scientific Neuromodulation is done so under a software license.  This file may not be copied or modified without
 * execution of the appropriate software license agreement with Boston Scientific Neuromodulation or explicit written
 * permission of Boston Scientific Neuromodulation.
 *
 * This file is provided as is, with no warranties of any kind including the warranties of design, merchantability and
 * fitness for a particular purpose, or arising from a course of dealing, usage or trade practice.
 *
 * Boston Scientific Neuromodulation shall have no liability with respect to the infringement of copyrights, trade
 * secrets or any patents by this file or any part thereof.
 *
 * In no event will Boston Scientific Neuromodulation be liable for any lost revenue or profits or other special,
 * indirect and consequential damages, even if Boston Scientific has been advised of the possibility of such damages,
 * as a result of the usage of this file and software for which this file is a part.
 * ********************************************************************************************************************
 *
 */
import * as React from "react";
import TablePagination from "@mui/material/TablePagination";
import { Dialog, DialogContent, Stack, TableBody, TableCell, TableRow, ThemeProvider, Typography } from "@mui/material";
import "./AdminHome.css"
import { TablePaginationView } from "../../../common/customviews/TablePaginationView";
import { GetClinicResponse } from "../../../../services/models/GetClinicResponse";
import { Clinic } from "../../../../services/models/Clinic";
import { BaseRowComponent } from "../../base/BaseRowComponent";
import { roboto500Text12 } from "../../../utils/theme/CommonTheme";
import { EditClinicView } from "./EditClinicView";
import { CustomToolTip } from "../../../common/customviews/CustomToolTip";


/**
 * An interface definition for Component Props
 */
type CompState = {
    redraw: boolean, editingName: string; showEditDialog: boolean; clinic?: Clinic
}

/**
 * An interface definition for Component Props
 */
interface Props {
    getClinicResponse: GetClinicResponse;
    onClinicEdited: any;
}

/**
 * A class definition for component CustomClinicRows
 */
export default class CustomClinicRows extends BaseRowComponent<Props, CompState>  { // eslint-disable-line

    protected loadMoreData() {
        // throw new Error("Method not implemented.");
    }

    protected shareStates() {
        this.redraw();
    }

    private redraw = ()=> {
        this.setState({
            redraw: !this.state.redraw
        });
    }

    constructor(props: Props) {
        super(props)
        // eslint-disable-next-line
        this.emptyRows = this.page > 0 ? Math.max(0, (1 + this.page) * this.rowsPerPage - this.props.getClinicResponse.count!!) : 0;
        this.state = {
            redraw : true, editingName : '', showEditDialog: false
        }
    }

    /**
     * Gets clinic name
     * @param clinic Clinic
     * @returns 
     */
    private getName = (clinic: Clinic)=> clinic.name

    /**
     * Gets clinic acccount number
     * @param clinic Clinic
     * @returns 
     */
    private getClinicAccountNumber = (clinic: Clinic)=>{
        return clinic.accountId? clinic.accountId : "NA";
    }

    /**
     * Dismisses the new clinic dialog
     */
    public dismissEditClinicDialog = () => {
        this.setState({ showEditDialog: false });
    }

    /**
     * Shows the new clinic dialog
     */
    public showEditClinicDialog = (clinic: Clinic) => {
        this.setState({ showEditDialog: true, clinic: clinic });
    }
    /**
     * Handles new clinic callback
     */
    private onClinicEdited = ()=> {
        this.setState({ showEditDialog: false});
        // update clinic
        this.props.onClinicEdited();
    }

    /**
     * Gets view of the clinic
     * @param clinic Clinic
     * @returns Grid
     */
    private getClinicRowView = (clinic: Clinic)=> {
        this.logger.verbose(`getClinicRowView(${JSON.stringify(clinic)})`, CustomClinicRows.name);
        this.index = this.index + 1;
        return <TableRow key={this.getRowRandomID()}
            sx={{ background: this.getRowBackground(), borderRadius: "10px", paddingLeft: "2vh", paddingRight: "2vh", marginTop: "1vh", minWidth: "100%", width: "100%", height: "7vh", alignItems: "center" }}>
            <TableCell sx={{ width: '40%' }} className="TableData">
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    marginRight={8}
                    spacing={2}>

                    <CustomToolTip title={this.getName(clinic) as string} toolTipLengthLimit={25}>
                        <Typography sx={this.TypographyStyle} style={{ textTransform: "capitalize" }}>
                            {
                                this.getName(clinic)
                            }
                        </Typography>
                    </CustomToolTip>

                    <img style={{ display : "block", height:"2.5vh", cursor: "pointer"}} src={"/../edit.svg"} onClick={()=>{
                        this.logger.verbose('Onclick(Edit Clinic)', CustomClinicRows.name);
                        this.props.getClinicResponse.rows!!.forEach((row : any)=> { // eslint-disable-line
                            if(clinic.id === row.id) {
                                row.isEditing  = true;
                                this.showEditClinicDialog(clinic);
                            } else {
                                row.isEditing = false;
                            }
                        });
                        this.redraw();
                    }}/>
                </Stack>
            </TableCell>
            <TableCell sx={{ width: '40%' }} className="TableData">
                <ThemeProvider theme={roboto500Text12}>
                    <Typography>
                        {this.getClinicAccountNumber(clinic)}
                    </Typography>
                </ThemeProvider>
            </TableCell>
            <TableCell sx={{ width: '20%' }} className="TableData">
                <ThemeProvider theme={roboto500Text12}>
                    <Typography>{this.getFormattedDate(clinic.createdAt)}</Typography>
                </ThemeProvider>
            </TableCell>
        </TableRow>
    }

    render() {

        return <React.Fragment>
            <TableBody sx={{ minWidth: 500}}>
                {(this.rowsPerPage > 0
                    ? this.props.getClinicResponse.rows!!.slice(this.page * this.rowsPerPage, this.page * this.rowsPerPage + this.rowsPerPage) //eslint-disable-line 
                    : this.props.getClinicResponse.rows!! //eslint-disable-line 
                ).map((row: any) => (
                    // eslint-disable-next-line
                    this.getClinicRowView(row)
                ))}
                {this.emptyRows > 0 && (
                    <TableRow style={{ height: 53 * this.emptyRows }}>
                        <TableCell colSpan={3} />
                    </TableRow>
                )}
                    <TableRow>
                        <TablePagination
                            // rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                            rowsPerPageOptions={[10]}
                            colSpan={3}
                            count={this.props.getClinicResponse?.count!!} //eslint-disable-line 
                            rowsPerPage={this.rowsPerPage}
                            page={this.page}
                            SelectProps={{
                                inputProps: {
                                    "aria-label": "rows per page",
                                },
                                native: true,
                            }}
                            onPageChange={this.handleChangePage}
                            onRowsPerPageChange={this.handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationView}
                        />
                    </TableRow>
            </TableBody>
           
            <Dialog onClose={this.dismissEditClinicDialog} aria-labelledby="customized-dialog-title" open={this.state.showEditDialog}
                maxWidth="xs" disableScrollLock={true} PaperProps={{ sx: { borderRadius: "1.25rem"} }}>
                <DialogContent style={{ justifyContent: "center", alignItems: "center", overflow: "hidden" }}>
                    <EditClinicView onDialogDismissed={this.dismissEditClinicDialog} onClinicEdited={this.onClinicEdited}  clinic={this.state.clinic}/>
                </DialogContent>
            </Dialog>
        </React.Fragment>
    }
}
